import React from 'react';
import { Link } from 'react-router-dom';
import SecurityBadges from './SecurityBadges';
import Logo from './Logo';
import CompetitorComparison from './CompetitorComparison';
import PricingCharts from './PricingCharts';

function Home() {
  return (
    <>
      {/* Navigation */}
      <nav className="flex justify-between items-center px-6 py-4 bg-white shadow-sm fixed top-0 w-full z-50">
        <div>
          <Link to="/" className="no-underline">
            <Logo height="32px" />
          </Link>
        </div>
        <div className="flex items-center gap-8">
          <a href="#pricing" className="text-gray-700 hover:text-blue-500 no-underline">Pricing</a>
          <Link to="/login" className="text-gray-700 hover:text-blue-500 no-underline">Login</Link>
          <Link to="/signup" className="px-6 py-3 rounded-xl bg-blue-500 hover:bg-blue-600 text-white font-semibold shadow no-underline">
            Get Started
          </Link>
        </div>
      </nav>

      {/* Hero Section */}
      <section className="bg-white pt-24">
        <div className="max-w-7xl mx-auto px-6 py-20 grid lg:grid-cols-2 gap-12 items-center">
          <div>
            <h1 className="text-5xl font-bold text-gray-900">
              Turn Every Incoming Call Into Commitment
            </h1>
            <p className="mt-6 text-lg text-gray-600">
              <b>No more missed calls. No more no-shows.</b> This AI answers questions, checks for availability, books, <b>collects and validates payments</b> — while still on the call. It walks your customer through the process and <b>validates payment instantly</b>, so you lock in commitment at the moment of peak interest.
Competitors can’t do that.



            </p>
            <div className="mt-8 flex gap-4">
              <Link to="/plans" className="px-6 py-3 rounded-xl bg-blue-500 hover:bg-blue-600 text-white font-semibold shadow no-underline">
                Get Started – From $99/mo
              </Link>
              <a href="#demo" className="px-6 py-3 rounded-xl border border-gray-300 bg-white text-gray-800 hover:bg-gray-100 no-underline">
                📞 Hear a Live Demo
              </a>
            </div>
          </div>
          <div className="lg:flex lg:justify-end">
            <div className="w-full max-w-md">
              <img 
                src="/assets/virtual_assistant.png" 
                alt="Virtual Assistant AI" 
                className="w-full h-auto rounded-2xl shadow-lg"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Competitor Comparison Section */}
      <CompetitorComparison />

      {/* Features Section */}
      <section className="bg-gray-50 py-20">
        <div className="max-w-7xl mx-auto px-6 text-center">
          <h2 className="text-3xl font-bold text-gray-900 mb-12">What Remote Assistant Does</h2>
          <div className="grid gap-8 sm:grid-cols-2 lg:grid-cols-4">
            <div className="bg-white p-6 rounded-2xl shadow-sm border">
              <div className="text-blue-500 text-3xl mb-4">📞</div>
              <h3 className="text-xl font-semibold text-gray-900 mb-2">Answer Calls & SMS</h3>
              <p className="text-gray-600">Never miss a customer inquiry, day or night.</p>
            </div>
            <div className="bg-white p-6 rounded-2xl shadow-sm border">
              <div className="text-blue-500 text-3xl mb-4">📅</div>
              <h3 className="text-xl font-semibold text-gray-900 mb-2">Book Appointments</h3>
              <p className="text-gray-600">Seamlessly integrates with Google or Outlook calendars.</p>
            </div>
            <div className="bg-white p-6 rounded-2xl shadow-sm border">
              <div className="text-blue-500 text-3xl mb-4">💳</div>
              <h3 className="text-xl font-semibold text-gray-900 mb-2">Collects Payments</h3>
              <p className="text-gray-600">Validate payments directly during calls with PCI compliance.</p>
            </div>
            <div className="bg-white p-6 rounded-2xl shadow-sm border">
              <div className="text-blue-500 text-3xl mb-4">✉️</div>
              <h3 className="text-xl font-semibold text-gray-900 mb-2">Send Confirmations</h3>
              <p className="text-gray-600">Automatic reminders reduce no-shows and cancellations.</p>
            </div>
          </div>
        </div>
      </section>

      {/* Pricing Section */}
      <section id="pricing" className="bg-white py-20">
        <div className="max-w-3xl mx-auto px-6 text-center">
          <h2 className="text-3xl font-bold text-gray-900 mb-4">Simple, Transparent Pricing</h2>
          <p className="text-gray-600 mb-10">Everything you need to replace a receptionist and never miss a call.</p>
          <div className="bg-white border rounded-2xl shadow-sm p-10">
            <div className="text-5xl font-bold text-gray-900">
              From $99<span className="text-xl font-medium text-gray-500">/mo</span>
            </div>
            <p className="mt-2 text-gray-500">Multiple plans available</p>
            <ul className="mt-6 space-y-2 text-gray-600">
              <li>✔ 24/7 Call & SMS Answering</li>
              <li>✔ Booking & Payment Handling</li>
              <li>✔ Confirmation Emails & SMS</li>
            </ul>
            <Link to="/plans" className="mt-8 inline-block px-8 py-4 rounded-xl bg-blue-500 hover:bg-blue-600 text-white font-semibold shadow no-underline">
              View Plans
            </Link>
          </div>
        </div>

        <PricingCharts />
      </section>

      {/* CTA Section */}
      <section className="bg-blue-500 text-white py-20">
        <div className="max-w-4xl mx-auto px-6 text-center">
          <h2 className="text-3xl font-bold text-white mb-6">Ready to Never Miss Another Customer?</h2>
          <Link to="/plans" className="inline-block px-8 py-4 rounded-xl bg-white text-blue-500 hover:bg-gray-100 font-semibold text-lg shadow no-underline">
            Start Free Trial
          </Link>
          <p className="mt-4 text-blue-100">14-day free trial • Credit card required • Cancel anytime</p>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-white border-t py-12">
        <div className="max-w-7xl mx-auto px-6 grid gap-12 md:grid-cols-3">
          <div>
            <h3 className="text-xl font-bold text-gray-900">Remote Assistant</h3>
            <p className="mt-4 text-gray-600">
              The AI-powered receptionist that answers calls, books appointments, and secures deposits — 24/7.
            </p>
          </div>
          <div className="grid grid-cols-2 gap-6">
            <div>
              <h4 className="text-sm font-semibold text-gray-800 uppercase mb-3">Product</h4>
              <ul className="space-y-2 text-gray-600">
                <li><a href="#features" className="hover:text-blue-500">Features</a></li>
                <li><a href="#pricing" className="hover:text-blue-500">Pricing</a></li>
                <li><a href="#demo" className="hover:text-blue-500">Demo</a></li>
              </ul>
            </div>
            <div>
              <h4 className="text-sm font-semibold text-gray-800 uppercase mb-3">Company</h4>
              <ul className="space-y-2 text-gray-600">
                <li><Link to="/privacy" className="hover:text-blue-500">Privacy</Link></li>
                <li><Link to="/toc" className="hover:text-blue-500">Terms</Link></li>
              </ul>
            </div>
          </div>
          <div>
            <SecurityBadges showInProgress={true} />
          </div>
        </div>
        <div className="mt-12 border-t pt-6 text-center text-sm text-gray-500">
          © 2025 Remote Assistant. All rights reserved.
        </div>
      </footer>
    </>
  );
}

export default Home;